import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const SECTIONS = [
  {
    title: "Who we are",
    body: [
      "Market Rounds Technologies is the data controller for the personal data collected through this website. You can reach us at the contact details at the bottom of this page.",
    ],
  },
  {
    title: "The data we collect",
    body: [
      "Contact enquiries — when you send us a message through the contact form, we collect your name, email address, company name and message so we can respond.",
      "Newsletter — when you subscribe, we collect your email address so we can send you insights and updates. You can unsubscribe at any time.",
      "Usage data — with your consent, we collect anonymous analytics about how the site is used (pages visited, time on site) to improve it. See our Cookie Policy for details and choices.",
    ],
  },
  {
    title: "How we use your data",
    body: [
      "We use personal data to respond to enquiries, provide our services, send requested communications, and improve this website. We only process your data where we have a lawful basis: your consent, the performance of a contract, or our legitimate business interests.",
      "We never sell your personal data, and we never share it with third parties for their own marketing.",
    ],
  },
  {
    title: "How long we keep it",
    body: [
      "Contact enquiries are kept only as long as needed to handle your request and any follow-up work. Newsletter subscriptions are kept until you unsubscribe. Analytics data is anonymised and kept in aggregate form.",
    ],
  },
  {
    title: "Who we share it with",
    body: [
      "We use a small number of trusted service providers to operate this site — for example email delivery and hosting. They process data on our behalf under appropriate safeguards and never for their own purposes.",
    ],
  },
  {
    title: "Your rights",
    body: [
      "Under UK GDPR you have the right to access, correct or delete your personal data, to object to or restrict processing, to data portability, and to withdraw consent at any time (without affecting anything done before withdrawal).",
      "To exercise any of these rights, email us and we will respond within one month. If you're not satisfied with our response, you have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk.",
    ],
  },
  {
    title: "Cookies",
    body: [
      "This site uses cookies as described in our Cookie Policy, where you can also change your preferences at any time.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "We may update this policy from time to time. Any changes will be posted on this page with a revised date.",
    ],
  },
];

const PrivacyPolicy = () => (
  <main data-testid="privacy-policy-page" className="pt-20 bg-black">
    <article className="rounded-t-[2.5rem] bg-white text-black py-24 lg:py-32 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <MaskedLine delay={0.1}>
          <Link
            to="/"
            data-testid="privacy-policy-back-link"
            className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-cyan-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </MaskedLine>
        <div className="mt-10">
          <Overline className="text-cyan-600">Legal</Overline>
        </div>
        <MaskedLine delay={0.2}>
          <h1 className="font-display tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4">
            Privacy Policy
          </h1>
        </MaskedLine>
        <p className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-6">
          Market Rounds Technologies · Last updated: September 2026
        </p>
        <div className="w-16 h-px bg-cyan-400 mt-8" />

        <Reveal className="mt-12 space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-display tracking-tight text-2xl">{s.title}</h2>
              <div className="space-y-4 mt-4">
                {s.body.map((p, i) => (
                  <p key={i} className="text-base text-zinc-600 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
          <section>
            <h2 className="font-display tracking-tight text-2xl">Contact us</h2>
            <p className="text-base text-zinc-600 leading-relaxed mt-4">
              Data protection queries:{" "}
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="privacy-policy-email-link"
                className="text-cyan-600 underline decoration-cyan-400/40 underline-offset-4 hover:decoration-cyan-500"
              >
                {CONTACT.email}
              </a>{" "}
              · {CONTACT.phone} · {CONTACT.address}
            </p>
          </section>
        </Reveal>
      </div>
    </article>
  </main>
);

export default PrivacyPolicy;
